import { Injectable, inject } from '@angular/core';
import { fhirR4 } from '@smile-cdr/fhirts';
import { TranslateService } from '@ngx-translate/core';
import get from 'lodash/get';
import { AppContext } from '../models/AppContext';
import { HomeNavState, UnsupportedTypeDetail } from '../models/HomeNavState';
import { ParamDetail } from '../models/ParamDetail';
import { ResourceType } from '../models/ResourceType';
import { EndpointService } from '../services/endpoint.service';
import { QrBuilderService } from './qr-builder.service';
import { QrPopulateService } from './qr-populate.service';
import { QuestionnaireService } from './questionnaire.service';
import { CoverageInformationService } from '../services/coverage-information.service';

/**
 * View-side callbacks the launch flow needs: the questionnaire must reach the
 * (hidden) form-renderer before population so unsupported items are reported in
 * time, and progress stages advance at the same points as before.
 */
export interface LaunchHooks {
  advance(): void;
  setQuestionnaire(questionnaire: fhirR4.Questionnaire): void;
  getUnsupportedDetail(): UnsupportedTypeDetail | undefined;
}

@Injectable({
  providedIn: 'root',
})
export class QuestionnaireLaunchService {
  private readonly endpointService = inject(EndpointService);
  private readonly qService = inject(QuestionnaireService);
  private readonly qrPopulateService = inject(QrPopulateService);
  private readonly qrBuilderService = inject(QrBuilderService);
  private readonly translate = inject(TranslateService);

  async launchWithQuestionnaire(
    appContext: AppContext,
    canonicalUrl: string | undefined,
    coverage: unknown,
    order: fhirR4.Resource,
    hooks: LaunchHooks,
  ): Promise<HomeNavState> {
    if (!this.isNonEmptyObject(coverage)) {
      throw new Error(this.translate.instant('Error.MissingCanonical'));
    }
    const parameters: ParamDetail[] = [];
    if (canonicalUrl) {
      parameters.push({
        name: 'questionnaire',
        type: 'valueCanonical',
        value: canonicalUrl,
      });
    }
    parameters.push({ name: 'coverage', type: 'resource', value: coverage });
    parameters.push({ name: 'order', type: 'resource', value: order });

    const context = CoverageInformationService.getCoverageAssertionId(order);
    if (context) {
      parameters.push({ name: 'context', type: 'valueString', value: context });
    }
    return this.runLaunch(appContext, parameters, hooks);
  }

  async cdexLaunch(
    task: fhirR4.Task,
    appContext: AppContext,
    hooks: LaunchHooks,
  ): Promise<HomeNavState> {
    const coverage = get(
      appContext,
      'marshalContext.marshalingRequest.Coverage',
    );
    const context = get(task, 'identifier[0].value');
    if (coverage && context) {
      return this.runLaunch(
        appContext,
        [
          { name: 'coverage', type: 'resource', value: coverage },
          { name: 'context', type: 'valueString', value: context },
        ],
        hooks,
        task,
      );
    }
    throw new Error(this.translate.instant('Error.MissingParameters'));
  }

  async runLaunch(
    appContext: AppContext,
    paramDetails: ParamDetail[],
    hooks: LaunchHooks,
    task?: fhirR4.Task,
  ): Promise<HomeNavState> {
    const { questionnaire, questionnaireResponse } =
      await this.callPackageAndPopulate(appContext, paramDetails, hooks);
    return this.buildNavigateState(
      questionnaireResponse,
      questionnaire,
      appContext,
      hooks,
      task,
    );
  }

  async callPackageAndPopulate(
    appContext: AppContext,
    paramDetails: ParamDetail[],
    hooks: LaunchHooks,
  ): Promise<{
    questionnaire: fhirR4.Questionnaire;
    questionnaireResponse: fhirR4.QuestionnaireResponse;
  }> {
    const qpackage = await this.callQPackage(paramDetails);
    const questionnaire = this.qService.processValueSet(qpackage);
    hooks.setQuestionnaire(questionnaire);
    hooks.advance();

    // Definition resources (Questionnaire, ValueSet, Library) are persisted to the
    // DEFAULT partition by the proxy during $questionnaire-package. No need to
    // POST the package from the client — HAPI FHIR's URL_BASED partitioning rejects
    // non-partitionable resources on tenant endpoints (HAPI-1318).
    const questionnaireResponse = await this.qrPopulateService.populate(
      qpackage,
      appContext,
    );
    return { questionnaire, questionnaireResponse };
  }

  async buildNavigateState(
    questionnaireResponse: fhirR4.QuestionnaireResponse,
    questionnaire: fhirR4.Questionnaire,
    appContext: AppContext,
    hooks: LaunchHooks,
    task?: fhirR4.Task,
  ): Promise<HomeNavState> {
    const unsupported = hooks.getUnsupportedDetail();
    let populateError;
    let qr = questionnaireResponse;
    try {
      if (!unsupported?.required || unsupported.required.length === 0) {
        qr = (await this.qrBuilderService.createDtrQr(
          questionnaireResponse,
          questionnaire,
          appContext,
        )) as fhirR4.QuestionnaireResponse;
        if (task) await this.qrBuilderService.addQRReferenceToTask(task, qr);
        else
          await this.qrBuilderService.addCoverageExtensionToOrder(
            appContext,
            qr,
          );
      }
      hooks.advance();
    } catch (err) {
      populateError = err;
    }
    return { questionnaire, questionnaireResponse: qr, populateError, task };
  }

  relaunch(
    questionnaireResponse: fhirR4.QuestionnaireResponse,
    hooks: LaunchHooks,
    task?: fhirR4.Task,
  ): HomeNavState {
    const questionnaire = questionnaireResponse.contained?.find(
      (resource) => resource.resourceType === ResourceType.Questionnaire,
    ) as fhirR4.Questionnaire;
    hooks.setQuestionnaire(questionnaire);
    hooks.advance();
    hooks.advance(); // skip populate step
    return { questionnaire, questionnaireResponse, task };
  }

  isValidPerformerType(questionnaire: fhirR4.Questionnaire): boolean {
    return this.qService.isValidPerformerType(questionnaire);
  }

  async callQPackage(paramDetails: ParamDetail[]): Promise<fhirR4.Bundle> {
    const packageEndpoint = this.endpointService.getQuestionnairePackageUrl();
    return await this.qService.callPackage(packageEndpoint, paramDetails);
  }

  private isNonEmptyObject(value: unknown): boolean {
    return (
      value !== null &&
      typeof value === 'object' &&
      Object.keys(value).length > 0
    );
  }
}
